import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

// App Check (reCAPTCHA Enterprise) — guards Firestore/Storage from non-app
// traffic. Only init in production builds; we don't enforce on Firestore/
// Storage yet, so dev doesn't need it and would otherwise spam the console
// with 403s until a debug token is registered. When enforcement is flipped
// on, register a debug token via Firebase Console → App Check → Apps →
// Manage debug tokens, and bring this back inside import.meta.env.DEV.
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;
if (recaptchaSiteKey && import.meta.env.PROD) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.warn('App Check init failed:', e);
  }
}

export const db = getFirestore(app);
export const storage = getStorage(app);
export const auth = getAuth(app);

// Analytics is opt-in: only initialize if a measurementId is configured and the
// browser supports it (e.g. not in SSR or restricted environments). track() is a
// no-op when analytics isn't available, so callers don't need to guard.
let analyticsInstance = null;
if (firebaseConfig.measurementId) {
  isSupported()
    .then((ok) => {
      if (ok) analyticsInstance = getAnalytics(app);
    })
    .catch(() => {});
}

export function track(eventName, params = {}) {
  if (!analyticsInstance) return;
  try {
    logEvent(analyticsInstance, eventName, params);
  } catch (e) {
    // Analytics failures must never break gameplay.
    console.warn('Analytics error:', e);
  }
}
